import { useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

function actionStyle(color: string, solid = false): React.CSSProperties {
  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
    minHeight: "44px",
    padding: "0 1rem",
    borderRadius: "10px",
    border: solid ? "none" : "1px solid var(--login-border)",
    background: solid ? color : "transparent",
    color: solid ? "#fff" : "var(--login-text)",
    fontFamily: "'DM Sans', sans-serif",
    fontWeight: 700,
    fontSize: "0.8rem",
    cursor: "pointer",
  };
}

export default function ReturnNoteModal({
  open,
  title,
  description,
  productName,
  supplierName,
  noteLabel,
  notePlaceholder,
  confirmLabel,
  confirmColor,
  extraContent,
  initialNote = "",
  noteRequired = true,
  pending = false,
  error,
  onClose,
  onConfirm,
}: {
  open: boolean;
  title: string;
  description: string;
  productName: string;
  supplierName: string;
  noteLabel: string;
  notePlaceholder: string;
  confirmLabel: string;
  confirmColor: string;
  extraContent?: ReactNode;
  initialNote?: string;
  noteRequired?: boolean;
  pending?: boolean;
  error?: string | null;
  onClose: () => void;
  onConfirm: (note: string) => Promise<void> | void;
}) {
  const [note, setNote] = useState(initialNote);
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setNote(initialNote);
      setLocalError(null);
    }
  }, [initialNote, open]);

  async function submit() {
    if (noteRequired && !note.trim()) {
      setLocalError("Informe uma nota para continuar.");
      return;
    }
    setLocalError(null);
    try {
      await onConfirm(note);
    } catch {
      // Parent error state is rendered through the optional error prop.
    }
  }

  useEffect(() => {
    if (!open) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape" && !pending) {
        onClose();
      }
      if (
        event.key === "Enter" &&
        !event.defaultPrevented &&
        !event.shiftKey &&
        !pending &&
        !(event.target instanceof HTMLButtonElement)
      ) {
        if (event.target instanceof HTMLTextAreaElement) event.preventDefault();
        submit();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, open, note, noteRequired, pending]);

  if (!open) return null;

  return createPortal(
    <>
      <style>{`
        @keyframes returnNoteOverlayIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes returnNotePanelIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .return-note-modal-overlay {
          animation: returnNoteOverlayIn 0.18s ease-out both;
        }
        .return-note-modal-panel {
          animation: returnNotePanelIn 0.22s cubic-bezier(0.16, 1, 0.3, 1) both;
        }
        .return-note-modal-field-label {
          font-family: 'DM Sans', sans-serif;
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--login-muted);
        }
        .return-note-modal-input {
          width: 100%;
          min-height: 44px;
          padding: 0.72rem 0.8rem;
          border-radius: 10px;
          border: 1px solid var(--login-border);
          background: var(--login-bg);
          color: var(--login-text);
          font-family: 'DM Sans', sans-serif;
          font-size: 0.98rem;
          font-weight: 700;
          box-sizing: border-box;
          outline: none;
        }
        .return-note-modal-input:focus {
          border-color: var(--login-accent);
          box-shadow: 0 0 0 3px color-mix(in srgb, var(--login-accent) 18%, transparent);
        }
      `}</style>

      <div
        className="return-note-modal-overlay"
        onClick={pending ? undefined : onClose}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 1200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding:
            "max(0.75rem, env(safe-area-inset-top)) 0.75rem max(0.75rem, env(safe-area-inset-bottom))",
          background: "rgba(7, 7, 7, 0.58)",
          backdropFilter: "blur(3px)",
        }}
      >
        <div
          className="return-note-modal-panel"
          onClick={(event) => event.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label={title}
          style={{
            width: "min(92vw, 560px)",
            maxHeight: "min(84vh, 760px)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            borderRadius: "14px",
            border:
              "1px solid color-mix(in srgb, var(--login-border) 78%, #ffffff 6%)",
            background: "var(--login-surface)",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.32)",
          }}
        >
          <div
            style={{
              padding: "1rem 1rem 0.9rem",
              borderBottom: "1px solid var(--login-border)",
              background: "var(--login-surface)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: "0.75rem",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <h2
                  style={{
                    margin: 0,
                    fontFamily: "'DM Sans', sans-serif",
                    fontWeight: 700,
                    fontSize: "1.2rem",
                    color: "var(--login-text)",
                  }}
                >
                  {title}
                </h2>
                {description ? (
                  <p
                    style={{
                      margin: "0.25rem 0 0",
                      color: "var(--login-muted)",
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: "0.82rem",
                      lineHeight: 1.35,
                    }}
                  >
                    {description}
                  </p>
                ) : null}
              </div>

              <button
                type="button"
                className="return-note-modal-close"
                onClick={onClose}
                aria-label="Fechar"
                disabled={pending}
                style={{
                  flexShrink: 0,
                  borderRadius: "0.5rem",
                  border: "none",
                  background: "transparent",
                  cursor: pending ? "default" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 0,
                  height: "44px",
                  width: "44px",
                }}
              >
                <X size={18} />
              </button>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr)",
                gap: "0.28rem",
                marginTop: "0.8rem",
              }}
            >
              <div
                style={{
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "var(--login-muted)",
                }}
              >
                Produto
              </div>
              <div
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: "0.96rem",
                  fontWeight: 600,
                  color: "var(--login-text)",
                  lineHeight: 1.3,
                  wordBreak: "break-word",
                }}
              >
                {productName}
              </div>
              <div
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: "0.84rem",
                  color: "var(--login-muted)",
                  lineHeight: 1.4,
                  wordBreak: "break-word",
                }}
              >
                {supplierName}
              </div>
            </div>
          </div>

          <div
            style={{
              padding: "1rem",
              display: "flex",
              flexDirection: "column",
              gap: "0.45rem",
              overflowY: "auto",
              background: "var(--login-surface)",
            }}
          >
            {extraContent ? (
              <div style={{ marginBottom: "0.8rem" }}>{extraContent}</div>
            ) : null}
            <label
              htmlFor="return-note-modal-textarea"
              className="return-note-modal-field-label"
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "0.75rem",
                fontWeight: 600,
                color: "var(--login-muted)",
              }}
            >
              {noteLabel}
            </label>
            <textarea
              id="return-note-modal-textarea"
              className="return-note-modal-textarea"
              value={note}
              onChange={(event) => {
                setNote(event.target.value);
                if (localError) setLocalError(null);
              }}
              placeholder={notePlaceholder}
              disabled={pending}
              style={{
                width: "100%",
                minHeight: "120px",
                padding: "0.85rem 0.9rem",
                borderRadius: "10px",
                border: "1px solid var(--login-border)",
                background: "var(--login-bg)",
                color: "var(--login-text)",
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "0.92rem",
                lineHeight: 1.45,
                resize: "vertical",
                boxSizing: "border-box",
                outline: "none",
              }}
            />
            {error || localError ? (
              <div
                style={{
                  color: "var(--login-error)",
                  fontSize: "0.78rem",
                  fontWeight: 700,
                }}
              >
                {error ?? localError}
              </div>
            ) : null}
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "0.6rem",
              padding: "0.95rem 1rem 1rem",
              borderTop: "1px solid var(--login-border)",
              background: "var(--login-surface)",
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={pending}
              style={actionStyle("var(--login-muted)")}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => submit()}
              disabled={pending}
              style={actionStyle(confirmColor, true)}
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
